import { createContext, useContext } from "react";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

export interface AuthValue {
  status: AuthStatus;
  /**
   * The client's ONLY auth surface. Firebase owns refresh scheduling; the portal never
   * inspects the token — no decode, no expiry arithmetic, no claim reads. A 401 is the only
   * signal that a refresh did not happen.
   */
  getToken: (forceRefresh?: boolean) => Promise<string | null>;
  /** GCIP's own view, used only by /verify's poll. Never a source of role or org. */
  emailVerified: boolean;
  email: string | null;
  signUpWithPassword: (email: string, password: string) => Promise<void>;
  /** Resolves to the account's emailVerified flag so the caller can route without a re-render. */
  signInWithPassword: (email: string, password: string, keepSignedIn: boolean) => Promise<boolean>;
  signInWithGoogle: (keepSignedIn: boolean) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  /** Resolves for unknown addresses too — never a signal that an account exists. */
  sendPasswordResetEmail: (email: string) => Promise<void>;
  reloadUser: () => Promise<boolean>;
  verifyPasswordResetCode: (code: string) => Promise<string>;
  confirmPasswordReset: (code: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}

/**
 * Firebase's error codes, mapped to our copy keys.
 *
 * Wrong password and unknown user produce ONE message. Distinguishing them is account
 * enumeration, and it is the single easiest privacy mistake to make on a login screen.
 */
export function authErrorKey(code: string): string {
  switch (code) {
    case "auth/email-already-in-use":
      return "error.auth.emailInUse";
    case "auth/weak-password":
      return "error.auth.weakPassword";
    case "auth/popup-closed-by-user":
    case "auth/cancelled-popup-request":
      return "error.auth.popupClosed";
    case "auth/expired-action-code":
    case "auth/invalid-action-code":
      return "error.auth.linkExpired";
    case "auth/too-many-requests":
      return "error.cooldown";
    case "auth/network-request-failed":
      return "error.network";
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/invalid-credential":
    case "auth/invalid-email":
      return "error.auth.badCredentials";
    default:
      return "error.auth.unavailable";
  }
}
